import { useVideoTracking } from '@ecomm/cdp-tracking'
import { useTrackingVideo } from '@ecomm/tracking/useTrackingVideo'
import classNames from 'classnames'
import type React from 'react'
import { type ReactHTMLElement, useRef } from 'react'

type HTMLVideoProps = Partial<ReactHTMLElement<HTMLVideoElement>>['props']
type EventProps = React.SyntheticEvent<HTMLVideoElement, Event>

export type VideoProps = HTMLVideoProps & {
  /** URL for the WebM video source format */
  readonly videoSrcUrlWebm: string
  /** URL for the MP4 video source format */
  readonly videoSrcUrlMp4: string
  readonly ref?: React.LegacyRef<HTMLVideoElement> | undefined
  readonly key?: React.Key
  /** CSS classes to apply to the video element */
  readonly className?: string
  /** CSS classes to apply to the video container wrapper */
  readonly containerClassName?: string
  /** URL for video captions/subtitles file */
  readonly captionsSrcUrl?: string | null
  /** A label for accessibility purposes, providing a short, descriptive text alternative for the video element. It will soon be marked as required */
  readonly ariaLabel?: string
}

/**
 * A native HTML video component with built-in tracking and accessibility features.
 *
 * This component wraps the native HTML5 video element with additional functionality including:
 * - Automatic video tracking for analytics (play, pause, progress, completion)
 * - Accessibility support with ARIA labels and captions
 * - Customizable styling through className and containerClassName props
 * - Built-in video controls with restricted options (no download, picture-in-picture, etc.)
 */
export function NativeVideo({
  ref = null,
  videoSrcUrlWebm,
  videoSrcUrlMp4,
  onPlay,
  onPause,
  onEnded,
  onTimeUpdate,
  containerClassName = '',
  key = 'video-element',
  captionsSrcUrl = '',
  className = '',
  autoPlay = false,
  ariaLabel,
  ...rest
}: VideoProps) {
  const customButtonRef = useRef<HTMLVideoElement>(null)
  const {
    handleTrackingVideoEnded,
    handleTrackingVideoPause,
    handleTrackingVideoPlay,
    handleTrackingVideoTimeUpdate
  } = useTrackingVideo()

  const {
    trackVideoPlay,
    trackVideoPause,
    trackVideoComplete,
    trackVideoProgress
  } = useVideoTracking()

  const onPlayVideo = (event: EventProps) => {
    handleTrackingVideoPlay(event)
    trackVideoPlay(event)
    onPlay && onPlay(event)
  }

  const onPauseVideo = (event: EventProps) => {
    handleTrackingVideoPause(event)
    trackVideoPause(event)
    onPause && onPause(event)
  }

  const onVideoEnded = (event: EventProps) => {
    handleTrackingVideoEnded(event)
    trackVideoComplete(event)
    onEnded && onEnded(event)
  }

  const onVideoTimeUpdate = (event: EventProps) => {
    handleTrackingVideoTimeUpdate(event)
    trackVideoProgress(event)
    onTimeUpdate && onTimeUpdate(event)
  }

  return (
    <div
      className={classNames(
        'rounded-base relative overflow-hidden',
        containerClassName
      )}
      key={key}
    >
      <video
        autoPlay={autoPlay}
        className={classNames('block w-full', className)}
        controls
        controlsList="nodownload nopictureinpicture noplaybackrate nospeed"
        crossOrigin="anonymous"
        data-component="native-video"
        disablePictureInPicture
        disableRemotePlayback
        muted
        ref={ref || customButtonRef}
        aria-label={ariaLabel}
        {...rest}
        onEnded={onVideoEnded}
        onPause={onPauseVideo}
        onPlay={onPlayVideo}
        onTimeUpdate={onVideoTimeUpdate}
      >
        <source src={videoSrcUrlWebm} type="video/webm" />
        <source src={videoSrcUrlMp4} type="video/mp4" />
        {captionsSrcUrl ? (
          <track
            default
            kind="captions"
            src={captionsSrcUrl}
            srcLang="en"
            label="English Captions"
          />
        ) : null}
      </video>
    </div>
  )
}
